<template>
  <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
    <div class="news-detail-pagination" v-if="newsList.length">
      <Swiper
        :modules="swiperModules"
        :slides-per-view="slidesPerView"
        :space-between="newsList.length === 1 ? 0 : 30"
        :centered-slides="false"
        class="news-detail-pagination-swiper"
        :class="{ 'news-detail-pagination-swiper--single': newsList.length === 1 }"
      >
        <SwiperSlide
          v-for="(item, index) in newsList"
          :key="index"
          class="news-detail-pagination-slide"
          :class="{ 'news-detail-pagination-slide--single': newsList.length === 1 }"
        >
          <site-link
            :link="{
              value: {
                href: item?.fields?.url?.value?.href,
                linktype: 'internal',
                text: item?.fields?.newsTitle?.value,
              },
            }"
            class="news-detail-pagination-link"
            :class="{ active: item.url === currentUrl }"
          >
            <JssRichText class="news-detail-pagination-title" :field="item?.fields?.newsTitle" />
          </site-link>
        </SwiperSlide>
      </Swiper>
      <button
        type="button"
        class="news-detail-pagination__control news-detail-pagination__control--prev"
        :disabled="!hasPrev"
        aria-label="Previous"
        @click="goToPrev"
      >
        <Icon name="page-prev" />
      </button>
      <button
        type="button"
        class="news-detail-pagination__control news-detail-pagination__control--next"
        :disabled="!hasNext"
        aria-label="Next"
        @click="goToNext"
      >
        <Icon name="page-next" />
      </button>
    </div>
  </ContentGrid>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { Swiper, SwiperSlide } from 'swiper/vue';
import Icon from '@/elements/Icon.vue';
import { getBetterUrl } from '@/utils/uri-utils';
import api from '@/api';

export default {
  name: 'NewsSwiper',
  components: {
    Swiper,
    SwiperSlide,
    Icon,
  },
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
    rendering: {
      type: Object,
    },
    page: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props) {
    const router = useRouter();
    const newsList = ref([]);
    const slidesPerView = ref(1);
    const currentUrl = ref('');

    const swiperModules = [];

    const hasPrev = computed(() => !!newsList.value[0]);
    const hasNext = computed(() => !!newsList.value[1]);

    const buildItemUrl = (item) => {
      if (!item?.url) return '';
      const { lang = 'en' } = router.currentRoute.value?.params ?? {};
      return getBetterUrl(item.fields.url.value.href, lang) ?? '';
    };

    const goToPrev = () => {
      const url = buildItemUrl(newsList.value[0]);
      if (url) router.push(url);
    };

    const goToNext = () => {
      const url = buildItemUrl(newsList.value[1]);
      if (url) router.push(url);
    };

    const checkDevice = () => {
      if (newsList.value.length <= 1) {
        slidesPerView.value = 1;
        return;
      }
      // Align with SCSS `tablet-landscape` breakpoint ($query-tablet-landscape: 880px)
      slidesPerView.value = typeof window !== 'undefined' && window.innerWidth >= 880 ? 2 : 1;
    };

    const getNews = async () => {
      const [res, ex] = await api.news.getNewsDetail({
        type: 'news',
        id: props.page.itemId,
      });
      if (ex) {
        newsList.value = [ex.previous, ex.next].filter(Boolean);
        checkDevice();
      }
    };

    onMounted(() => {
      getNews();
      checkDevice();
      if (typeof window !== 'undefined') {
        currentUrl.value = window.location.pathname;
        window.addEventListener('resize', checkDevice);
      }
    });

    onUnmounted(() => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', checkDevice);
      }
    });

    return {
      swiperModules,
      newsList,
      slidesPerView,
      currentUrl,
      hasPrev,
      hasNext,
      goToPrev,
      goToNext,
    };
  },
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.news-detail-pagination {
  position: relative;
  margin-top: 32px;
  padding: 16px 0;

  &-swiper {
    width: 100%;
    padding: 8px 30px;
    margin: 0 auto;
  }

  &-slide {
    flex-shrink: 0;
    width: calc(100% - 32px);

    &--single {
      width: 100% !important;
      max-width: 100%;
      flex: 0 0 100%;
    }
  }

  &-link {
    text-decoration: none;
    white-space: wrap;

    &:hover,
    &:focus-visible {
      color: #739b78;
      outline: none;
    }

    &.active {
      .news-detail-pagination-title {
        color: $blue-night;
      }
    }

    .news-detail-pagination-title {
      white-space: wrap;
    }
  }

  &-title {
    display: block;
    overflow: hidden;
    font-size: 14px;
    line-height: 1.2;
    font-weight: 400;
    letter-spacing: 0;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__control {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: none;
    background: transparent;
    color: #404040;
    cursor: pointer;
    padding: 0;
    transition: color 0.2s;

    &--prev {
      left: -12px;
    }

    &--next {
      right: -12px;
    }

    .e-icon {
      width: 24px;
      height: 24px;
    }

    &:hover,
    &:focus-visible {
      color: #739b78;
      outline: none;
    }

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }
  }

  @include tablet-landscape {
    &-title {
      font-size: 18px;
    }

    &-swiper {
      padding: 8px 60px;
    }

    &-slide {
      flex-shrink: 0;
      width: calc(50% - 32px);

      &--single {
        width: 100% !important;
        max-width: 100%;
        flex: 0 0 100%;
      }
    }

    &__control {
      &--prev {
        left: -12px;
      }

      &--next {
        right: -12px;
      }
    }
  }
}
</style>
